import { NwButton } from "@nawy/ui";
import FiltersIcon from "@/modules/common/components/ui-kit/icons/filters-icon";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";
import { Box, Typography, useTheme } from "@mui/material";
import { useState, useEffect } from "react";
import SortInputToggle, {
  SortOrder,
} from "@/modules/common/components/input/sort-input-toggle";
import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";

interface MobileRatingFilterMenuProps {
  ratingFilterHookResult: any;
}

const MobileRatingFilterMenu: React.FC<MobileRatingFilterMenuProps> = ({
  ratingFilterHookResult,
}) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);

  // Local temporary state for the filters (only committed on Apply)
  const [tempRating, setTempRating] = useState(
    ratingFilterHookResult.selectedOption
  );
  const [tempSortOrder, setTempSortOrder] = useState<SortOrder>(
    ratingFilterHookResult.sortOrder
  );

  // Sync temporary state with parent when modal opens
  useEffect(() => {
    if (modalOpen) {
      setTempRating(ratingFilterHookResult.selectedOption);
      setTempSortOrder(ratingFilterHookResult.sortOrder);
    }
  }, [
    modalOpen,
    ratingFilterHookResult.selectedOption,
    ratingFilterHookResult.sortOrder,
  ]);

  const handleModalConfirmation = () => {
    // Apply the temporary filters to the parent state
    ratingFilterHookResult.handleChange(tempRating);
    ratingFilterHookResult.handleSortChange(tempSortOrder);
    setModalOpen(false);
  };

  const clearFilters = () => {
    // Reset both temporary and parent filters to default values
    const defaultOption = ratingFilterHookResult.options[0];
    const defaultSort: SortOrder = "newest";

    setTempRating(defaultOption);
    setTempSortOrder(defaultSort);
    ratingFilterHookResult.handleChange(defaultOption);
    ratingFilterHookResult.handleSortChange(defaultSort);
    setModalOpen(false);
  };

  return (
    <>
      <NwButton
        variant="tonal"
        sx={{
          width: "fit-content",
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          border: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
        }}
        onClick={() => setModalOpen(true)}
      >
        <FiltersIcon stroke={theme.nawy.colors.semantic.text.primary} />
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: theme.nawy.colors.semantic.text.primary,
            backgroundColor: theme.nawy.colors.semantic.layer.primary,
          }}
        >
          {"Filters"}
        </Typography>
      </NwButton>
      <MobileModal
        open={modalOpen}
        title="Filters"
        confirmButtonText="Apply filters"
        cancelButtonText="Clear filters"
        onConfirm={handleModalConfirmation}
        onClose={() => setModalOpen(false)}
        onCancel={clearFilters}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.md,
            padding: theme.nawy.spacing.md,
          }}
        >
          {/* Sort Filter */}
          <Box>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.semibold,
                color: theme.nawy.colors.semantic.text.secondary,
                marginBottom: theme.nawy.spacing.xs,
              }}
            >
              Sort
            </Typography>
            <SortInputToggle
              sortOrder={tempSortOrder}
              onChange={setTempSortOrder}
              fullWidth
            />
          </Box>

          {/* Rating Filter */}
          <Box>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.semibold,
                color: theme.nawy.colors.semantic.text.secondary,
                marginBottom: theme.nawy.spacing.xs,
              }}
            >
              Rating
            </Typography>
            <CustomAutoComplete
              options={ratingFilterHookResult.options}
              value={tempRating}
              onChange={setTempRating}
              placeholder={ratingFilterHookResult.placeholder}
              fullWidth
              loading={ratingFilterHookResult.loading}
            />
          </Box>
        </Box>
      </MobileModal>
    </>
  );
};

export default MobileRatingFilterMenu;
